
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Worksheet } from "@/hooks/worksheet/types";
import { WorksheetFormFields } from "./WorksheetFormFields";
import { worksheetFormSchema, WorksheetFormValues } from "./worksheetFormSchema";

interface WorksheetEditDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  worksheet: Worksheet | null;
  onSave: (data: WorksheetFormValues) => void;
}

export const WorksheetEditDialog: React.FC<WorksheetEditDialogProps> = ({
  isOpen,
  setIsOpen,
  worksheet,
  onSave
}) => {
  const form = useForm<WorksheetFormValues>({
    resolver: zodResolver(worksheetFormSchema),
    defaultValues: {
      name: "",
      subject: "",
      grade_level: "",
      semester: "",
      fs_name: "",
      document_urls: [], // Initialize as empty array to match schema
      status: ""
    }
  });

  useEffect(() => {
    if (worksheet) {
      // Ensure document_urls is always an array
      const documentUrls = Array.isArray(worksheet.document_urls) 
        ? worksheet.document_urls 
        : worksheet.document_urls 
          ? [worksheet.document_urls] 
          : [];
        
      form.reset({
        name: worksheet.name || "",
        subject: worksheet.subject || "",
        grade_level: worksheet.grade_level || "",
        semester: worksheet.semester || "",
        fs_name: worksheet.fs_name || "",
        document_urls: documentUrls,
        status: worksheet.status || ""
      });
    }
  }, [worksheet, form]);

  const handleSubmit = (data: WorksheetFormValues) => {
    onSave(data);
  };

  const handleClose = () => {
    setIsOpen(false);
    form.reset();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>แก้ไขข้อมูลชีท</DialogTitle>
          <DialogDescription>
            แก้ไขรายละเอียดชีทการสอน ID: {worksheet?.id}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <WorksheetFormFields form={form} />
            
            <DialogFooter>
              <Button variant="outline" type="button" onClick={handleClose}>ยกเลิก</Button>
              <Button type="submit">บันทึก</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
